import type { CategoryBreakdown } from '@apply/lending-limits';
import { CoreRows } from '../components/lendingLimitAccordionPanel/components/common/constants/tables/rows/CoreRows';
import { ICoreTableRow } from '../components/lendingLimitAccordionPanel/components/common/constants/tables/interfaces/ICoreTableRow';
import { EMPTY_ROW, combineRows, detailRow, fromBucket } from './shared';

/**
 * Core grid rows: the three core product buckets, a sub-total, the bridge overflow, and
 * the combined Core total.
 *
 * "External" is externally verified project experience. The BE reports it per principal
 * rather than per loan type, so the whole count sits on the Construction row and repeats
 * on the sub-total. It has no backing loans — the count comes from the principal's
 * `Experiences`, which are not loans — so `externalLoans` is always undefined and the
 * External eye icon has nothing to open.
 */
export function toCoreRows(breakdown: CategoryBreakdown): ICoreTableRow[] {
    const construction = fromBucket(breakdown.buckets.construction);
    const fixFlip = fromBucket(breakdown.buckets.fixFlip);
    const land = fromBucket(breakdown.buckets.land);
    const subTotal = combineRows([construction, fixFlip, land]);

    // The overflow carries no repayments of its own — those stay on the Bridge table —
    // and it is backed by the retained bridge loans that produced it.
    const excessBridge = {
        ...EMPTY_ROW,
        outstandingAmount: breakdown.bridge.excess,
        outstandingLoans:
            breakdown.bridge.excess > 0 ? breakdown.buckets.retainedBridge.outstanding.loans : [],
    };

    const external = breakdown.externalExperienceCount;

    return [
        { summaryItemType: CoreRows.Construction, ...detailRow(construction), externalNumber: external },
        { summaryItemType: CoreRows.FixFlip, ...detailRow(fixFlip), externalNumber: 0 },
        { summaryItemType: CoreRows.Land, ...detailRow(land), externalNumber: 0 },
        { summaryItemType: CoreRows.SubTotal, ...detailRow(subTotal), externalNumber: external },
        { summaryItemType: CoreRows.ExcessBridge, ...detailRow(excessBridge), externalNumber: 0 },
        {
            summaryItemType: CoreRows.Core,
            ...detailRow(combineRows([subTotal, excessBridge])),
            externalNumber: 0,
        },
    ];
}
